import { useEffect, useMemo, useState } from 'react'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { adminApi } from '@/services/adminApi'
import {
  Building2,
  Check,
  Info,
  Loader2,
  Plus,
  RadioTower,
  RotateCcw,
  Search,
  ShieldAlert,
  ToggleLeft,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import type {
  FeatureFlagMetadata,
  FeatureFlagOrganizationSummary,
  FeatureFlagOverrideState,
  FeatureFlagsGlobalResponse,
  MeetingRecordingProviderChoice,
  MeetingRecordingRoutingMode,
  MeetingRoutingHealth,
  OrganizationFeatureFlagRow,
  OrganizationFeatureFlagsResponse,
} from '@/types/admin'

type ConsoleMode = 'global' | 'organization'
type ProviderField = 'googleMeetProvider' | 'zoomProvider' | 'teamsProvider'
type DeadlineField = 'adHocFallbackDeadlineSeconds' | 'scheduledFallbackDeadlineSeconds'
type OrganizationFlagFilter = 'all' | 'overridden' | 'inherited' | 'force_on' | 'force_off'

const PROVIDERS: Array<{ field: ProviderField; label: string; description: string }> = [
  {
    field: 'googleMeetProvider',
    label: 'Google Meet',
    description: 'Native browser bot is available when launcher and neutral reads are ready.',
  },
  {
    field: 'zoomProvider',
    label: 'Zoom',
    description: 'Native requires a connected Zoom account; otherwise backend falls back to Recall.',
  },
  {
    field: 'teamsProvider',
    label: 'Microsoft Teams',
    description: 'Teams native is not available yet, so Recall remains the only valid choice.',
  },
]

function errorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'string') return error
  const responseError = (error as { response?: { data?: { error?: unknown; message?: unknown } } })?.response?.data?.error
  const responseMessage = (error as { response?: { data?: { message?: unknown } } })?.response?.data?.message
  const directMessage = error instanceof Error ? error.message : undefined
  const objectMessage =
    error && typeof error === 'object' && 'message' in error
      ? (error as { message?: unknown }).message
      : undefined
  const value = responseError ?? responseMessage ?? directMessage ?? objectMessage
  return typeof value === 'string' ? value : fallback
}

function statusPill(label: string, ok: boolean) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ${ok ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
      {label}
    </span>
  )
}

function valuePill(enabled: boolean, label?: string) {
  return (
    <span className={`inline-flex min-w-14 items-center justify-center rounded-md px-2.5 py-1 text-xs font-semibold ${enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
      {label ?? (enabled ? 'ON' : 'OFF')}
    </span>
  )
}

function formatDate(value: string | null) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString()
}

function overrideActionLabel(action: FeatureFlagOverrideState) {
  if (action === 'inherit') return 'Inherit global default'
  if (action === 'force_on') return 'Force on'
  return 'Force off'
}

function metadataByKey(metadata: FeatureFlagMetadata[]): Record<string, FeatureFlagMetadata> {
  return Object.fromEntries(metadata.map(flag => [flag.key, flag]))
}

export default function FeatureFlagsSettingsPage() {
  const [mode, setMode] = useState<ConsoleMode>('global')
  const [loading, setLoading] = useState(true)
  const [globalData, setGlobalData] = useState<FeatureFlagsGlobalResponse | null>(null)
  const [pendingFlag, setPendingFlag] = useState<string | null>(null)

  const [orgQuery, setOrgQuery] = useState('')
  const [orgResults, setOrgResults] = useState<FeatureFlagOrganizationSummary[]>([])
  const [totalOrganizations, setTotalOrganizations] = useState(0)
  const [matchingOrganizations, setMatchingOrganizations] = useState(0)
  const [orgSearchLoading, setOrgSearchLoading] = useState(false)
  const [selectedOrgs, setSelectedOrgs] = useState<FeatureFlagOrganizationSummary[]>([])
  const [selectedOrg, setSelectedOrg] = useState<FeatureFlagOrganizationSummary | null>(null)
  const [orgFlags, setOrgFlags] = useState<OrganizationFeatureFlagsResponse | null>(null)
  const [orgFlagsLoading, setOrgFlagsLoading] = useState(false)
  const [pendingOverride, setPendingOverride] = useState<string | null>(null)
  const [bulkFlagKey, setBulkFlagKey] = useState('')
  const [bulkAction, setBulkAction] = useState<FeatureFlagOverrideState>('force_on')
  const [bulkApplying, setBulkApplying] = useState(false)
  const [syncingOrgId, setSyncingOrgId] = useState<string | null>(null)
  const [bulkSyncing, setBulkSyncing] = useState(false)

  const [routingHealth, setRoutingHealth] = useState<MeetingRoutingHealth | null>(null)
  const [pendingRoutingField, setPendingRoutingField] = useState<string | null>(null)
  const [deadlineDrafts, setDeadlineDrafts] = useState<Record<DeadlineField, string>>({
    adHocFallbackDeadlineSeconds: '',
    scheduledFallbackDeadlineSeconds: '',
  })

  const metadata = globalData?.metadata ?? []
  const metadataMap = useMemo(() => metadataByKey(metadata), [metadata])
  const overrideableMetadata = useMemo(
    () => metadata.filter(flag => flag.overrideable),
    [metadata],
  )
  const enabledCount = metadata.filter(flag => globalData?.flags[flag.key] === true).length

  useEffect(() => {
    if (overrideableMetadata.length === 0) {
      if (bulkFlagKey) setBulkFlagKey('')
      return
    }
    if (!bulkFlagKey || !overrideableMetadata.some(flag => flag.key === bulkFlagKey)) {
      setBulkFlagKey(overrideableMetadata[0].key)
    }
  }, [bulkFlagKey, overrideableMetadata])

  const applyRoutingHealth = (health: MeetingRoutingHealth) => {
    setRoutingHealth(health)
    setDeadlineDrafts({
      adHocFallbackDeadlineSeconds: String(health.policy.adHocFallbackDeadlineSeconds),
      scheduledFallbackDeadlineSeconds: String(health.policy.scheduledFallbackDeadlineSeconds),
    })
  }

  const loadGlobalFlags = async () => {
    const data = await adminApi.getFeatureFlags()
    setGlobalData(data)
  }

  const loadOrganizationInventory = async (query = orgQuery) => {
    const result = await adminApi.getFeatureFlagOrganizations(query.trim(), 50)
    setOrgResults(result.organizations)
    setTotalOrganizations(result.totalOrganizations)
    setMatchingOrganizations(result.matchingCount)
    return result.organizations
  }

  useEffect(() => {
    Promise.all([
      loadGlobalFlags(),
      adminApi.getMeetingRoutingHealth().then(applyRoutingHealth),
    ])
      .catch((e: unknown) => toast.error(errorMessage(e, 'Failed to load feature flag console')))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    if (mode !== 'organization') return
    const query = orgQuery.trim()

    setOrgSearchLoading(true)
    const timer = window.setTimeout(() => {
      loadOrganizationInventory(query)
        .then(organizations => {
          if (!selectedOrg && organizations.length > 0) {
            refreshOrganizationFlags(organizations[0])
          }
        })
        .catch((e: unknown) => toast.error(errorMessage(e, 'Failed to search organizations')))
        .finally(() => setOrgSearchLoading(false))
    }, 250)

    return () => window.clearTimeout(timer)
  }, [mode, orgQuery])

  const refreshOrganizationFlags = async (org: FeatureFlagOrganizationSummary) => {
    setOrgFlagsLoading(true)
    try {
      const data = await adminApi.getOrganizationFeatureFlags(org.id)
      setSelectedOrg(data.organization)
      setOrgFlags(data)
    } catch (e: unknown) {
      toast.error(errorMessage(e, 'Failed to load organization feature flags'))
    } finally {
      setOrgFlagsLoading(false)
    }
  }

  const handlePreviewOrganization = async (org: FeatureFlagOrganizationSummary) => {
    await refreshOrganizationFlags(org)
  }

  const handleToggleOrganizationSelection = (org: FeatureFlagOrganizationSummary) => {
    setSelectedOrgs(prev =>
      prev.some(selected => selected.id === org.id)
        ? prev.filter(selected => selected.id !== org.id)
        : [...prev, org],
    )
  }

  const handleRemoveSelectedOrganization = async (organizationId: string) => {
    const nextSelected = selectedOrgs.filter(org => org.id !== organizationId)
    setSelectedOrgs(nextSelected)
  }

  const handleSelectAllLoadedOrganizations = () => {
    if (orgResults.length === 0) return
    setSelectedOrgs(prev => {
      const selectedIds = new Set(prev.map(org => org.id))
      return [
        ...prev,
        ...orgResults.filter(org => !selectedIds.has(org.id)),
      ]
    })
  }

  const handleGlobalToggle = async (flag: FeatureFlagMetadata) => {
    if (!globalData) return
    const enabled = !globalData.flags[flag.key]
    setPendingFlag(flag.key)
    try {
      const updated = await adminApi.updateFeatureFlags({ [flag.key]: enabled })
      setGlobalData(updated)
      if (flag.key === 'onlineRecordingNeutralReads') {
        adminApi.getMeetingRoutingHealth()
          .then(applyRoutingHealth)
          .catch((e: unknown) => toast.error(errorMessage(e, 'Failed to refresh meeting routing')))
      }
      if (selectedOrg) {
        await refreshOrganizationFlags(selectedOrg)
      }
      toast.success(`${flag.label} ${enabled ? 'enabled' : 'disabled'}`)
    } catch (e: unknown) {
      toast.error(errorMessage(e, `Failed to update ${flag.label}`))
    } finally {
      setPendingFlag(null)
    }
  }

  const handleOverride = async (
    row: OrganizationFeatureFlagRow,
    action: 'inherit' | 'force_on' | 'force_off',
  ) => {
    if (!selectedOrg || !row.overrideable) return
    setPendingOverride(row.key)
    try {
      const updated =
        action === 'inherit'
          ? await adminApi.clearOrganizationFeatureFlagOverride(selectedOrg.id, row.key)
          : await adminApi.setOrganizationFeatureFlagOverride(
              selectedOrg.id,
              row.key,
              action === 'force_on',
            )
      setSelectedOrg(updated.organization)
      setOrgFlags(updated)
      toast.success(`${row.label} override updated`)
    } catch (e: unknown) {
      toast.error(errorMessage(e, `Failed to update ${row.label}`))
    } finally {
      setPendingOverride(null)
    }
  }

  const handleBulkOverride = async () => {
    const flag = metadataMap[bulkFlagKey]
    if (selectedOrgs.length === 0) {
      toast.error('Select at least one organization')
      return
    }
    if (!flag?.overrideable) {
      toast.error('Select an organization-overrideable flag')
      return
    }

    setBulkApplying(true)
    try {
      const result = await adminApi.bulkUpdateOrganizationFeatureFlagOverrides(
        selectedOrgs.map(org => org.id),
        bulkFlagKey,
        bulkAction,
      )
      if (selectedOrg) {
        await refreshOrganizationFlags(selectedOrg)
      }
      await loadOrganizationInventory()
      toast.success(`${flag.label}: ${overrideActionLabel(bulkAction)} applied to ${result.updatedCount} organizations`)
    } catch (e: unknown) {
      toast.error(errorMessage(e, `Failed to bulk update ${flag.label}`))
    } finally {
      setBulkApplying(false)
    }
  }

  const handleSyncOrganization = async () => {
    if (!selectedOrg) return

    setSyncingOrgId(selectedOrg.id)
    try {
      const updated = await adminApi.syncOrganizationFeatureFlagOverrides(selectedOrg.id)
      setSelectedOrg(updated.organization)
      setOrgFlags(updated)
      await loadOrganizationInventory()
      toast.success(`${updated.organization.name} now inherits current global config`)
    } catch (e: unknown) {
      toast.error(errorMessage(e, 'Failed to sync organization with global config'))
    } finally {
      setSyncingOrgId(null)
    }
  }

  const handleBulkSyncOrganizations = async () => {
    if (selectedOrgs.length === 0) {
      toast.error('Select at least one organization')
      return
    }

    setBulkSyncing(true)
    try {
      const result = await adminApi.bulkSyncOrganizationFeatureFlagOverrides(
        selectedOrgs.map(org => org.id),
      )
      if (selectedOrg) {
        await refreshOrganizationFlags(selectedOrg)
      }
      await loadOrganizationInventory()
      toast.success(`${result.syncedCount} organizations now inherit current global config`)
    } catch (e: unknown) {
      toast.error(errorMessage(e, 'Failed to sync selected organizations with global config'))
    } finally {
      setBulkSyncing(false)
    }
  }

  const handleRoutingPatch = async (
    field: string,
    patch: Parameters<typeof adminApi.updateMeetingRoutingPolicy>[0],
  ) => {
    setPendingRoutingField(field)
    try {
      await adminApi.updateMeetingRoutingPolicy(patch)
      const health = await adminApi.getMeetingRoutingHealth()
      applyRoutingHealth(health)
      toast.success('Meeting routing updated')
    } catch (e: unknown) {
      toast.error(errorMessage(e, 'Failed to update meeting routing'))
    } finally {
      setPendingRoutingField(null)
    }
  }

  const handleDeadlineBlur = (field: DeadlineField) => {
    if (!routingHealth) return
    const next = Number(deadlineDrafts[field])
    const current = routingHealth.policy[field]
    if (next === current) return
    if (!Number.isInteger(next) || next < 15 || next > 600) {
      toast.error('Fallback deadline must be an integer between 15 and 600 seconds')
      setDeadlineDrafts(prev => ({ ...prev, [field]: String(current) }))
      return
    }
    handleRoutingPatch(field, { [field]: next })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    )
  }

  const policy = routingHealth?.policy

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <ToggleLeft className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Feature Flag Console</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {enabledCount}/{metadata.length} global defaults enabled
            </p>
          </div>
        </div>

        <div className="inline-flex overflow-hidden rounded-md border border-gray-200 bg-white">
          {([
            ['global', 'Global Defaults'],
            ['organization', 'Organization Overrides'],
          ] as Array<[ConsoleMode, string]>).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              className={`h-9 border-r border-gray-200 px-3 text-sm font-semibold last:border-r-0 ${mode === value ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {mode === 'global' ? (
        <GlobalDefaultsTable
          metadata={metadata}
          flags={globalData?.flags ?? {}}
          pendingFlag={pendingFlag}
          onToggle={handleGlobalToggle}
        />
      ) : (
        <OrganizationOverridesTable
          query={orgQuery}
          onQueryChange={setOrgQuery}
          results={orgResults}
          searchLoading={orgSearchLoading}
          totalOrganizations={totalOrganizations}
          matchingOrganizations={matchingOrganizations}
          selectedOrgs={selectedOrgs}
          selectedOrg={selectedOrg}
          orgFlags={orgFlags}
          orgFlagsLoading={orgFlagsLoading}
          pendingOverride={pendingOverride}
          overrideableMetadata={overrideableMetadata}
          metadataMap={metadataMap}
          bulkFlagKey={bulkFlagKey}
          bulkAction={bulkAction}
          bulkApplying={bulkApplying}
          syncingOrgId={syncingOrgId}
          bulkSyncing={bulkSyncing}
          onPreviewOrg={handlePreviewOrganization}
          onToggleOrgSelection={handleToggleOrganizationSelection}
          onRemoveSelectedOrg={handleRemoveSelectedOrganization}
          onSelectAllLoadedOrgs={handleSelectAllLoadedOrganizations}
          onBulkFlagKeyChange={setBulkFlagKey}
          onBulkActionChange={setBulkAction}
          onBulkApply={handleBulkOverride}
          onSyncOrg={handleSyncOrganization}
          onBulkSync={handleBulkSyncOrganizations}
          onOverride={handleOverride}
        />
      )}

      {mode === 'global' && routingHealth && policy && (
        <MeetingRoutingPanel
          routingHealth={routingHealth}
          pendingRoutingField={pendingRoutingField}
          deadlineDrafts={deadlineDrafts}
          setDeadlineDrafts={setDeadlineDrafts}
          handleDeadlineBlur={handleDeadlineBlur}
          handleRoutingPatch={handleRoutingPatch}
        />
      )}
    </div>
  )
}

function GlobalDefaultsTable({
  metadata,
  flags,
  pendingFlag,
  onToggle,
}: {
  metadata: FeatureFlagMetadata[]
  flags: Record<string, boolean>
  pendingFlag: string | null
  onToggle: (flag: FeatureFlagMetadata) => void
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 text-sm">
      <div className="grid grid-cols-[1fr_150px_170px] border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-400">
        <div className="px-4 py-2.5">Feature</div>
        <div className="border-l border-gray-200 px-4 py-2.5 text-center">Global</div>
        <div className="border-l border-gray-200 px-4 py-2.5">Scope</div>
      </div>

      {metadata.map(flag => {
        const isOn = flags[flag.key] === true
        const isPending = pendingFlag === flag.key
        return (
          <div key={flag.key} className="grid grid-cols-[1fr_150px_170px] border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60">
            <div className="px-4 py-3.5">
              <div className="flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${isOn ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="font-medium text-gray-900">{flag.label}</span>
              </div>
              <p className="ml-4 mt-0.5 text-xs text-gray-500">{flag.description}</p>
              {flag.dependencyWarnings.length > 0 && (
                <div className="ml-4 mt-2 flex items-start gap-1.5 text-xs text-amber-700">
                  <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{flag.dependencyWarnings.map(w => w.message).join(' ')}</span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-center border-l border-gray-100">
              <button
                type="button"
                disabled={pendingFlag !== null}
                onClick={() => onToggle(flag)}
                className={`inline-flex h-8 min-w-20 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${isOn ? 'bg-green-50 text-green-700 hover:bg-green-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isOn ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                {isOn ? 'ON' : 'OFF'}
              </button>
            </div>
            <div className="border-l border-gray-100 px-4 py-3.5 text-xs text-gray-500">
              {flag.overrideable ? 'Org overrideable' : flag.globalOnlyReason ?? 'Global only'}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function OrganizationOverridesTable({
  query,
  onQueryChange,
  results,
  searchLoading,
  totalOrganizations,
  matchingOrganizations,
  selectedOrgs,
  selectedOrg,
  orgFlags,
  orgFlagsLoading,
  pendingOverride,
  overrideableMetadata,
  metadataMap,
  bulkFlagKey,
  bulkAction,
  bulkApplying,
  syncingOrgId,
  bulkSyncing,
  onPreviewOrg,
  onToggleOrgSelection,
  onRemoveSelectedOrg,
  onSelectAllLoadedOrgs,
  onBulkFlagKeyChange,
  onBulkActionChange,
  onBulkApply,
  onSyncOrg,
  onBulkSync,
  onOverride,
}: {
  query: string
  onQueryChange: (value: string) => void
  results: FeatureFlagOrganizationSummary[]
  searchLoading: boolean
  totalOrganizations: number
  matchingOrganizations: number
  selectedOrgs: FeatureFlagOrganizationSummary[]
  selectedOrg: FeatureFlagOrganizationSummary | null
  orgFlags: OrganizationFeatureFlagsResponse | null
  orgFlagsLoading: boolean
  pendingOverride: string | null
  overrideableMetadata: FeatureFlagMetadata[]
  metadataMap: Record<string, FeatureFlagMetadata>
  bulkFlagKey: string
  bulkAction: FeatureFlagOverrideState
  bulkApplying: boolean
  syncingOrgId: string | null
  bulkSyncing: boolean
  onPreviewOrg: (org: FeatureFlagOrganizationSummary) => void
  onToggleOrgSelection: (org: FeatureFlagOrganizationSummary) => void
  onRemoveSelectedOrg: (organizationId: string) => void
  onSelectAllLoadedOrgs: () => void
  onBulkFlagKeyChange: (key: string) => void
  onBulkActionChange: (action: FeatureFlagOverrideState) => void
  onBulkApply: () => void
  onSyncOrg: () => void
  onBulkSync: () => void
  onOverride: (
    row: OrganizationFeatureFlagRow,
    action: 'inherit' | 'force_on' | 'force_off',
  ) => void
}) {
  const [flagFilter, setFlagFilter] = useState<OrganizationFlagFilter>('all')
  const rows = orgFlags?.flags ?? []
  const overrideRows = rows.filter(row => row.override.state !== 'inherit')
  const forceOnCount = rows.filter(row => row.override.state === 'force_on').length
  const forceOffCount = rows.filter(row => row.override.state === 'force_off').length
  const inheritedCount = rows.filter(row => row.override.state === 'inherit').length
  const effectiveOnCount = rows.filter(row => row.effectiveValue).length
  const selectedOrgIds = new Set(selectedOrgs.map(org => org.id))
  const loadedSelectedCount = results.filter(org => selectedOrgIds.has(org.id)).length
  const allLoadedSelected = results.length > 0 && loadedSelectedCount === results.length
  const currentOrgSelected = selectedOrg ? selectedOrgIds.has(selectedOrg.id) : false
  const orgSyncing = selectedOrg ? syncingOrgId === selectedOrg.id : false
  const bulkBusy = bulkApplying || bulkSyncing
  const bulkDisabled = selectedOrgs.length === 0
  const selectedOverrideCount = selectedOrgs.reduce(
    (total, org) => total + (org.overrideCount ?? 0),
    0,
  )
  const visibleSelectedOrgs = selectedOrgs.slice(0, 3)
  const hiddenSelectedOrgCount = Math.max(selectedOrgs.length - visibleSelectedOrgs.length, 0)
  const visibleRows = rows.filter(row => {
    if (flagFilter === 'overridden') return row.override.state !== 'inherit'
    if (flagFilter === 'inherited') return row.override.state === 'inherit'
    if (flagFilter === 'force_on') return row.override.state === 'force_on'
    if (flagFilter === 'force_off') return row.override.state === 'force_off'
    return true
  })
  const filterOptions: Array<[OrganizationFlagFilter, string, number]> = [
    ['all', 'All', rows.length],
    ['overridden', 'Overrides', overrideRows.length],
    ['inherited', 'Inherited', inheritedCount],
    ['force_on', 'Forced on', forceOnCount],
    ['force_off', 'Forced off', forceOffCount],
  ]

  return (
    <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
      <aside className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="border-b border-gray-200 p-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Organizations</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                Showing {matchingOrganizations} of {totalOrganizations}
              </p>
            </div>
            {searchLoading && <Loader2 className="h-4 w-4 animate-spin text-blue-600" />}
          </div>
          <div className="relative mt-3">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={query}
              onChange={event => onQueryChange(event.target.value)}
              placeholder="Search name, domain, or organization id"
              className="h-10 w-full rounded-md border border-gray-200 pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
            />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-md bg-gray-50 px-2.5 py-2">
              <div className="font-semibold text-gray-900">{selectedOrgs.length}</div>
              <div className="text-gray-500">selected</div>
            </div>
            <div className="rounded-md bg-gray-50 px-2.5 py-2">
              <div className="font-semibold text-gray-900">{results.length}</div>
              <div className="text-gray-500">loaded</div>
            </div>
          </div>
          <button
            type="button"
            disabled={results.length === 0 || allLoadedSelected}
            onClick={onSelectAllLoadedOrgs}
            className="mt-2 inline-flex h-8 w-full items-center justify-center rounded-md border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Select all
          </button>
        </div>

        <div className="max-h-[640px] overflow-y-auto">
          {results.length === 0 && !searchLoading ? (
            <div className="px-4 py-10 text-center text-sm text-gray-500">
              No organizations match this search.
            </div>
          ) : (
            results.map(org => {
              const isBulkSelected = selectedOrgIds.has(org.id)
              const isPreviewed = selectedOrg?.id === org.id
              return (
                <div
                  key={org.id}
                  className={`flex gap-3 border-b border-gray-100 px-3 py-3 last:border-b-0 ${isPreviewed ? 'bg-blue-50/70' : 'hover:bg-gray-50'}`}
                >
                  <button
                    type="button"
                    onClick={() => onToggleOrgSelection(org)}
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${isBulkSelected ? 'border-blue-500 bg-blue-600 text-white' : 'border-gray-300 bg-white text-transparent hover:border-blue-400'}`}
                    aria-label={`${isBulkSelected ? 'Remove' : 'Select'} ${org.name} for bulk apply`}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onPreviewOrg(org)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <Building2 className="h-4 w-4 shrink-0 text-gray-400" />
                      <span className="truncate text-sm font-semibold text-gray-900">{org.name}</span>
                    </div>
                    <div className="mt-1 truncate text-xs text-gray-500">{org.emailDomain}</div>
                    <div className="mt-0.5 truncate text-[11px] text-gray-400">{org.id}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                        {org.overrideCount ?? 0} overrides
                      </span>
                      {(org.forceOnCount ?? 0) > 0 && (
                        <span className="rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700">
                          {org.forceOnCount} on
                        </span>
                      )}
                      {(org.forceOffCount ?? 0) > 0 && (
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-700">
                          {org.forceOffCount} off
                        </span>
                      )}
                    </div>
                  </button>
                </div>
              )
            })
          )}
        </div>
      </aside>

      <section className="min-w-0 space-y-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          {selectedOrg ? (
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                  Organization config
                </div>
                <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2">
                  <h2 className="truncate text-lg font-semibold text-gray-900">{selectedOrg.name}</h2>
                  {selectedOrg.verified && (
                    <span className="rounded-md bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                      Verified
                    </span>
                  )}
                  {selectedOrg.isPersonal && (
                    <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      Personal
                    </span>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-500">{selectedOrg.emailDomain} - {selectedOrg.id}</div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  disabled={orgSyncing || overrideRows.length === 0}
                  onClick={onSyncOrg}
                  title={overrideRows.length === 0 ? 'This organization already inherits global config' : 'Clear all organization overrides'}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {orgSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                  Sync to global
                </button>
                <button
                  type="button"
                  onClick={() => onToggleOrgSelection(selectedOrg)}
                  className={`inline-flex h-9 items-center justify-center gap-2 rounded-md border px-3 text-sm font-semibold ${currentOrgSelected ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  {currentOrgSelected ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {currentOrgSelected ? 'Selected for bulk' : 'Add to bulk'}
                </button>
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-gray-500">
              Select an organization to view its effective feature flag configuration.
            </div>
          )}

          {selectedOrg && (
            <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {[
                ['Overrides', overrideRows.length],
                ['Forced on', forceOnCount],
                ['Forced off', forceOffCount],
                ['Effective on', effectiveOnCount],
              ].map(([label, value]) => (
                <div key={label} className="rounded-md bg-gray-50 px-3 py-2">
                  <div className="text-lg font-semibold text-gray-900">{value}</div>
                  <div className="text-xs font-medium text-gray-500">{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-4 py-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">Bulk actions</div>
              <div className="mt-0.5 text-xs text-gray-500">
                {selectedOrgs.length} selected - {selectedOverrideCount} overrides
              </div>
            </div>
          </div>

          <div className="grid gap-3 px-4 py-3 lg:grid-cols-[minmax(180px,1fr)_minmax(190px,240px)_auto_auto_auto] lg:items-end">
            <div className="min-w-0">
              {selectedOrgs.length > 0 ? (
                <div className="flex max-h-16 flex-wrap gap-1.5 overflow-y-auto pr-1">
                  {visibleSelectedOrgs.map(org => (
                    <span
                      key={org.id}
                      className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700"
                    >
                      <button
                        type="button"
                        onClick={() => onPreviewOrg(org)}
                        className="max-w-36 truncate text-left"
                        title={`${org.emailDomain} - ${org.id}`}
                      >
                        {org.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveSelectedOrg(org.id)}
                        className="rounded p-0.5 text-blue-500 hover:bg-blue-100 hover:text-blue-700"
                        aria-label={`Remove ${org.name}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  ))}
                  {hiddenSelectedOrgCount > 0 && (
                    <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-600">
                      +{hiddenSelectedOrgCount} more
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex h-9 items-center rounded-md border border-dashed border-gray-200 px-3 text-xs font-medium text-gray-400">
                  No organizations selected
                </div>
              )}
            </div>

            <label className="block min-w-0">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Flag</span>
              <select
                value={bulkFlagKey}
                disabled={bulkBusy || bulkDisabled}
                onChange={event => onBulkFlagKeyChange(event.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2.5 text-sm font-medium text-gray-900 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400"
              >
                {overrideableMetadata.map(flag => (
                  <option key={flag.key} value={flag.key}>{flag.label}</option>
                ))}
              </select>
            </label>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Override</div>
              <div className="mt-1 inline-flex overflow-hidden rounded-md border border-gray-200 bg-white">
                {([
                  ['inherit', 'Inherit', RotateCcw],
                  ['force_on', 'On', Check],
                  ['force_off', 'Off', X],
                ] as const).map(([action, label, Icon]) => (
                  <button
                    key={action}
                    type="button"
                    disabled={bulkBusy || bulkDisabled}
                    onClick={() => onBulkActionChange(action)}
                    className={`inline-flex h-9 min-w-18 items-center justify-center gap-1.5 border-r border-gray-200 px-2.5 text-xs font-semibold last:border-r-0 disabled:cursor-not-allowed disabled:opacity-50 ${bulkAction === action ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              disabled={bulkBusy || bulkDisabled || !bulkFlagKey}
              onClick={onBulkApply}
              className="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-blue-600 px-3.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {bulkApplying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Apply to {selectedOrgs.length}
            </button>

            <button
              type="button"
              disabled={bulkBusy || bulkDisabled}
              onClick={onBulkSync}
              title="Clear all overrides for selected organizations"
              className="inline-flex h-9 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-3.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {bulkSyncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              Sync selected
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white text-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Feature configuration</h3>
              <p className="mt-0.5 text-xs text-gray-500">
                Global default, organization override, and effective value for the selected org.
              </p>
            </div>
            <div className="inline-flex overflow-hidden rounded-md border border-gray-200 bg-white">
              {filterOptions.map(([value, label, count]) => (
                <button
                  key={value}
                  type="button"
                  disabled={!selectedOrg}
                  onClick={() => setFlagFilter(value)}
                  className={`h-8 border-r border-gray-200 px-2.5 text-xs font-semibold last:border-r-0 disabled:cursor-not-allowed disabled:opacity-50 ${flagFilter === value ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  {label} {selectedOrg ? count : ''}
                </button>
              ))}
            </div>
          </div>

          {!selectedOrg ? (
            <div className="px-4 py-12 text-center text-sm text-gray-500">
              Choose an organization from the list to inspect its feature flags.
            </div>
          ) : orgFlagsLoading && !orgFlags ? (
            <div className="flex items-center justify-center px-4 py-12">
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-[minmax(220px,1fr)_92px_230px_100px_190px] border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-400">
                <div className="px-4 py-2.5">Feature</div>
                <div className="border-l border-gray-200 px-3 py-2.5 text-center">Global</div>
                <div className="border-l border-gray-200 px-3 py-2.5 text-center">Override</div>
                <div className="border-l border-gray-200 px-3 py-2.5 text-center">Effective</div>
                <div className="border-l border-gray-200 px-3 py-2.5">Audit / notes</div>
              </div>

              {visibleRows.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-500">
                  No flags match this filter.
                </div>
              ) : (
                visibleRows.map(row => {
                  const isPending = pendingOverride === row.key
                  const meta = metadataMap[row.key] ?? row
                  const changedAt = formatDate(row.override.updatedAt)
                  return (
                    <div key={row.key} className="grid grid-cols-[minmax(220px,1fr)_92px_230px_100px_190px] border-b border-gray-100 last:border-b-0 hover:bg-gray-50/60">
                      <div className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${row.effectiveValue ? 'bg-green-500' : 'bg-gray-300'}`} />
                          <span className="font-medium text-gray-900">{row.label}</span>
                        </div>
                        <p className="ml-4 mt-0.5 text-xs text-gray-500">{meta.description}</p>
                      </div>
                      <div className="flex items-center justify-center border-l border-gray-100">
                        {valuePill(row.globalValue)}
                      </div>
                      <div className="flex items-center justify-center border-l border-gray-100 px-3">
                        <div className="inline-flex overflow-hidden rounded-md border border-gray-200 bg-white">
                          {([
                            ['inherit', 'Inherit', RotateCcw],
                            ['force_on', 'On', Check],
                            ['force_off', 'Off', X],
                          ] as const).map(([action, label, Icon]) => {
                            const active = row.override.state === action
                            return (
                              <button
                                key={action}
                                type="button"
                                disabled={!row.overrideable || pendingOverride !== null || orgSyncing}
                                onClick={() => onOverride(row, action)}
                                className={`inline-flex h-8 min-w-16 items-center justify-center gap-1 border-r border-gray-200 px-2 text-xs font-semibold last:border-r-0 disabled:cursor-not-allowed disabled:opacity-50 ${active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
                                title={!row.overrideable ? row.globalOnlyReason ?? 'Global only' : label}
                              >
                                {isPending && active ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
                                {label}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                      <div className="flex items-center justify-center border-l border-gray-100">
                        {valuePill(row.effectiveValue)}
                      </div>
                      <div className="border-l border-gray-100 px-3 py-3 text-xs text-gray-500">
                        {!row.overrideable && (
                          <div className="flex items-start gap-1.5">
                            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>{row.globalOnlyReason}</span>
                          </div>
                        )}
                        {row.dependencyWarnings.length > 0 && (
                          <div className="flex items-start gap-1.5 text-amber-700">
                            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>{row.dependencyWarnings.map(w => w.message).join(' ')}</span>
                          </div>
                        )}
                        {row.override.state !== 'inherit' && (
                          <div>
                            <div className="font-medium text-gray-700">
                              {row.override.updatedByEmail ?? row.override.updatedByUserId ?? 'Unknown admin'}
                            </div>
                            {changedAt && <div className="mt-0.5 text-gray-400">{changedAt}</div>}
                          </div>
                        )}
                        {row.override.state === 'inherit' && row.overrideable && (
                          <span className="text-gray-400">Inherited from global default</span>
                        )}
                      </div>
                    </div>
                  )
                })
              )}
            </>
          )}
        </div>
      </section>
    </div>
  )
}

function MeetingRoutingPanel({
  routingHealth,
  pendingRoutingField,
  deadlineDrafts,
  setDeadlineDrafts,
  handleDeadlineBlur,
  handleRoutingPatch,
}: {
  routingHealth: MeetingRoutingHealth
  pendingRoutingField: string | null
  deadlineDrafts: Record<DeadlineField, string>
  setDeadlineDrafts: Dispatch<SetStateAction<Record<DeadlineField, string>>>
  handleDeadlineBlur: (field: DeadlineField) => void
  handleRoutingPatch: (
    field: string,
    patch: Parameters<typeof adminApi.updateMeetingRoutingPolicy>[0],
  ) => void
}) {
  const policy = routingHealth.policy
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <RadioTower className="h-5 w-5 shrink-0 text-blue-600" />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">Meeting Recording Routing</h2>
            <p className="mt-0.5 text-xs text-gray-500">Global native/Recall policy for new meeting bots.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {statusPill('Neutral reads', routingHealth.neutralReadsEnabled)}
          {statusPill('Launcher', routingHealth.launcherConfigured)}
          {statusPill('Policy', routingHealth.valid)}
        </div>
      </div>

      {routingHealth.validation && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
          {errorMessage(routingHealth.validation.error, 'Meeting routing policy is invalid')}
        </div>
      )}

      <RoutingRow label="Mode" notes="Recall-only is the current safe default. Native first uses the provider choices below.">
        <SegmentedControl
          disabled={pendingRoutingField !== null}
          value={policy.mode}
          options={[
            ['recall_only', 'Recall only'],
            ['native_first', 'Native first'],
          ]}
          pending={pendingRoutingField === 'mode'}
          onChange={mode => handleRoutingPatch('mode', { mode: mode as MeetingRecordingRoutingMode })}
        />
      </RoutingRow>

      {PROVIDERS.map(provider => {
        const nativeAvailable =
          provider.field === 'googleMeetProvider'
            ? routingHealth.nativeCapabilities.googleMeet
            : provider.field === 'zoomProvider'
              ? routingHealth.nativeCapabilities.zoom
              : routingHealth.nativeCapabilities.teams
        return (
          <RoutingRow key={provider.field} label={provider.label} notes={provider.description}>
            <SegmentedControl
              disabled={pendingRoutingField !== null}
              value={policy[provider.field]}
              options={[
                ['recall', 'Recall'],
                ['native', 'Native'],
              ]}
              disabledValues={nativeAvailable ? [] : ['native']}
              pending={pendingRoutingField === provider.field}
              onChange={choice => handleRoutingPatch(provider.field, { [provider.field]: choice as MeetingRecordingProviderChoice })}
            />
          </RoutingRow>
        )
      })}

      <RoutingRow label="Fallback" notes="Recall fallback before native recording starts.">
        <SegmentedControl
          disabled={pendingRoutingField !== null}
          value={String(policy.fallbackEnabled)}
          options={[
            ['true', 'Enabled'],
            ['false', 'Disabled'],
          ]}
          pending={pendingRoutingField === 'fallbackEnabled'}
          onChange={enabled => handleRoutingPatch('fallbackEnabled', { fallbackEnabled: enabled === 'true' })}
        />
      </RoutingRow>

      <RoutingRow label="Deadlines" notes="Seconds before pre-recording fallback expires. Allowed range: 15-600.">
        <div className="flex flex-wrap gap-3">
          {([
            ['adHocFallbackDeadlineSeconds', 'Ad-hoc'],
            ['scheduledFallbackDeadlineSeconds', 'Scheduled'],
          ] as Array<[DeadlineField, string]>).map(([field, label]) => (
            <label key={field} className="flex items-center gap-2 text-xs text-gray-600">
              <span className="font-medium text-gray-700">{label}</span>
              <input
                type="number"
                min={15}
                max={600}
                step={15}
                disabled={pendingRoutingField !== null}
                value={deadlineDrafts[field]}
                onChange={event => setDeadlineDrafts(prev => ({ ...prev, [field]: event.target.value }))}
                onBlur={() => handleDeadlineBlur(field)}
                className="h-8 w-24 rounded-md border border-gray-200 px-2 text-sm text-gray-900 disabled:opacity-50"
              />
            </label>
          ))}
        </div>
      </RoutingRow>
    </div>
  )
}

function RoutingRow({
  label,
  notes,
  children,
}: {
  label: string
  notes: string
  children: ReactNode
}) {
  return (
    <div className="grid grid-cols-1 border-b border-gray-100 last:border-b-0 md:grid-cols-[220px_280px_1fr]">
      <div className="px-4 py-3 font-medium text-gray-900">{label}</div>
      <div className="px-4 py-3 md:border-l md:border-gray-100">{children}</div>
      <div className="px-4 pb-3 text-xs text-gray-500 md:border-l md:border-gray-100 md:py-3">{notes}</div>
    </div>
  )
}

function SegmentedControl({
  value,
  options,
  disabled,
  disabledValues = [],
  pending,
  onChange,
}: {
  value: string
  options: Array<[string, string]>
  disabled: boolean
  disabledValues?: string[]
  pending: boolean
  onChange: (value: string) => void
}) {
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-gray-200 bg-white">
      {options.map(([optionValue, label]) => {
        const active = value === optionValue
        const optionDisabled = disabled || disabledValues.includes(optionValue)
        return (
          <button
            key={optionValue}
            type="button"
            disabled={optionDisabled}
            onClick={() => onChange(optionValue)}
            className={`inline-flex h-8 items-center gap-1.5 border-r border-gray-200 px-3 text-xs font-semibold last:border-r-0 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${active ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'}`}
          >
            {pending && active ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : active ? <Check className="h-3.5 w-3.5" /> : null}
            {label}
          </button>
        )
      })}
    </div>
  )
}
